import { getApiError } from '../lib/apiError';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Target, Pencil, Loader2, TrendingUp, CalendarDays } from 'lucide-react';
import { checkAndCelebrateDailyGoal } from './GoalCelebration';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const Gauge = ({ pct, color, label, value, goal, testId }) => {
  const capped = Math.min(pct, 100);
  const r = 54;
  const circ = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center" data-testid={testId}>
      <div className="relative w-36 h-36">
        <svg viewBox="0 0 128 128" className="w-full h-full -rotate-90">
          <circle cx="64" cy="64" r={r} fill="none" stroke="#E2E8F0" strokeWidth="12" />
          <circle
            cx="64" cy="64" r={r} fill="none"
            stroke={color} strokeWidth="12" strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={circ - (capped / 100) * circ}
            style={{ transition: 'stroke-dashoffset 0.8s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-slate-800">{pct.toFixed(0)}%</span>
          <span className="text-[10px] text-slate-500">atingido</span>
        </div>
      </div>
      <p className="mt-2 text-sm font-medium text-slate-700">{label}</p>
      <p className="text-xs text-slate-500">{formatCurrency(value)} de {formatCurrency(goal)}</p>
    </div>
  );
};

export const GoalsSection = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [progress, setProgress] = useState(null);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [goalForm, setGoalForm] = useState({ monthly_goal: '', seller_goals: {} });

  const fetchProgress = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/goals/progress`);
      setProgress(res.data);
      return res.data;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      const data = await fetchProgress();
      if (data?.has_goal) {
        checkAndCelebrateDailyGoal();
        const key = `goal-alert-${data.month}-${new Date().toLocaleDateString('pt-BR')}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          setAlertOpen(true);
        }
      }
    })();
  }, []);

  const openGoalDialog = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/sellers`);
      setSellers(res.data || []);
    } catch (e) {
      setSellers([]);
    }
    const sellerGoalsMap = {};
    (progress?.seller_progress || []).forEach(sp => {
      sellerGoalsMap[sp.seller_id] = String(sp.monthly_goal);
    });
    setGoalForm({
      monthly_goal: progress?.monthly_goal ? String(progress.monthly_goal) : '',
      seller_goals: sellerGoalsMap
    });
    setGoalDialogOpen(true);
  };

  const handleSaveGoal = async () => {
    const monthlyGoal = parseFloat(goalForm.monthly_goal);
    if (!monthlyGoal || monthlyGoal <= 0) {
      toast.error('Informe uma meta mensal válida');
      return;
    }
    setSaving(true);
    try {
      const sellerGoals = sellers
        .filter(s => parseFloat(goalForm.seller_goals[s.id]) > 0)
        .map(s => ({
          seller_id: s.id,
          seller_name: s.name,
          monthly_goal: parseFloat(goalForm.seller_goals[s.id])
        }));
      await axios.put(`${API_URL}/api/dashboard/goals`, {
        monthly_goal: monthlyGoal,
        seller_goals: sellerGoals
      });
      toast.success('Meta salva com sucesso!');
      setGoalDialogOpen(false);
      fetchProgress();
    } catch (e) {
      toast.error(getApiError(e, 'Erro ao salvar meta'));
    } finally {
      setSaving(false);
    }
  };

  const monthLabel = new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  const dailyGoalPreview = parseFloat(goalForm.monthly_goal) > 0
    ? parseFloat(goalForm.monthly_goal) / 30 : 0;

  return (
    <>
      <Card className="border border-slate-200" data-testid="goals-section">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Target size={20} className="text-blue-600" />
            Metas de Vendas <span className="text-sm font-normal text-slate-500 capitalize">— {monthLabel}</span>
          </CardTitle>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={openGoalDialog} data-testid="set-goal-btn">
              <Pencil size={14} className="mr-2" />
              Definir Meta
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {progress?.has_goal ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Gauge
                  pct={progress.daily_pct}
                  color={progress.daily_pct >= 100 ? '#059669' : '#2563EB'}
                  label="Meta Diária"
                  value={progress.today_total}
                  goal={progress.daily_goal}
                  testId="daily-goal-gauge"
                />
                <Gauge
                  pct={progress.monthly_pct}
                  color={progress.monthly_pct >= 100 ? '#059669' : '#7C3AED'}
                  label="Meta Mensal"
                  value={progress.month_total}
                  goal={progress.monthly_goal}
                  testId="monthly-goal-gauge"
                />
              </div>

              {progress.seller_progress?.length > 0 && (
                <div className="space-y-3 pt-4 border-t" data-testid="seller-goals-list">
                  <p className="text-sm font-medium text-slate-700">Metas por Vendedor</p>
                  {progress.seller_progress.map((sp) => (
                    <div key={sp.seller_id} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium text-slate-700">{sp.seller_name}</span>
                        <span className="text-slate-500">
                          {formatCurrency(sp.month_total)} / {formatCurrency(sp.monthly_goal)}
                          <span className={`ml-2 font-semibold ${sp.monthly_pct >= 100 ? 'text-emerald-600' : 'text-blue-600'}`}>
                            {sp.monthly_pct.toFixed(0)}%
                          </span>
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${sp.monthly_pct >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                          style={{ width: `${Math.min(sp.monthly_pct, 100)}%` }}
                        />
                      </div>
                      <p className="text-[11px] text-slate-400">
                        Hoje: {formatCurrency(sp.today_total)} de {formatCurrency(sp.daily_goal)} ({sp.daily_pct.toFixed(0)}%)
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500" data-testid="no-goal-message">
              <Target size={36} className="mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Nenhuma meta definida para este mês</p>
              {isAdmin ? (
                <Button className="mt-4 bg-blue-600 hover:bg-blue-700" onClick={openGoalDialog} data-testid="set-first-goal-btn">
                  <Target size={16} className="mr-2" />
                  Definir Meta do Mês
                </Button>
              ) : (
                <p className="text-xs mt-1">Peça ao administrador para definir a meta</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Definir Meta */}
      <Dialog open={goalDialogOpen} onOpenChange={setGoalDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-auto" aria-describedby={undefined} data-testid="goal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target size={18} className="text-blue-600" />
              Definir Meta — <span className="capitalize">{monthLabel}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Meta do Mês (empresa)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={goalForm.monthly_goal}
                onChange={(e) => setGoalForm({ ...goalForm, monthly_goal: e.target.value })}
                placeholder="Ex: 60000"
                className="text-lg h-12"
                data-testid="monthly-goal-input"
              />
              {dailyGoalPreview > 0 && (
                <p className="text-sm text-blue-600 flex items-center gap-1" data-testid="daily-goal-preview">
                  <CalendarDays size={14} />
                  Meta diária: <strong>{formatCurrency(dailyGoalPreview)}</strong> (meta ÷ 30 dias)
                </p>
              )}
            </div>

            {sellers.length > 0 && (
              <div className="space-y-2 pt-2 border-t">
                <Label>Metas por Vendedor (opcional)</Label>
                {sellers.map((s) => (
                  <div key={s.id} className="flex items-center gap-2">
                    <span className="flex-1 text-sm text-slate-700 truncate">{s.name}</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={goalForm.seller_goals[s.id] || ''}
                      onChange={(e) => setGoalForm({
                        ...goalForm,
                        seller_goals: { ...goalForm.seller_goals, [s.id]: e.target.value }
                      })}
                      placeholder="0,00"
                      className="w-32"
                      data-testid={`seller-goal-input-${s.id}`}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setGoalDialogOpen(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleSaveGoal} disabled={saving} data-testid="save-goal-btn">
                {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Target size={16} className="mr-2" />}
                Salvar Meta
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alerta da meta diária ao entrar */}
      <Dialog open={alertOpen} onOpenChange={setAlertOpen}>
        <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="daily-goal-alert">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target size={20} className="text-blue-600" />
              Meta de Hoje
            </DialogTitle>
          </DialogHeader>
          {progress && (
            <div className="space-y-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{formatCurrency(progress.daily_goal)}</p>
              <div className="p-3 bg-slate-50 rounded-lg space-y-1">
                <p className="text-sm text-slate-600">
                  Vendido hoje: <strong className="text-slate-800">{formatCurrency(progress.today_total)}</strong>
                </p>
                <p className={`text-sm font-medium ${progress.daily_pct >= 100 ? 'text-emerald-600' : 'text-slate-600'}`}>
                  {progress.daily_pct >= 100
                    ? '🎉 Meta do dia atingida! Parabéns!'
                    : `Faltam ${formatCurrency(Math.max(progress.daily_goal - progress.today_total, 0))} para a meta (${progress.daily_pct.toFixed(0)}%)`}
                </p>
              </div>
              <p className="text-xs text-slate-400 flex items-center justify-center gap-1">
                <TrendingUp size={12} />
                Meta mensal: {formatCurrency(progress.monthly_goal)} — {progress.monthly_pct.toFixed(0)}% atingido
              </p>
              <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => setAlertOpen(false)} data-testid="close-goal-alert-btn">
                Vamos vender! 💪
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
